// ***************************************************************************************
// Board construction, ported from game/puzzlegen.cpp and game/puzzle.cpp.
//
// Puzzle mode's board is CONSTRUCTED, not dealt. The solution is built first and the
// board falls out of it:
//
//   1. Repeatedly carve a random 8-connected path of 3 to 6 tiles out of the grid and
//      SETTLE what remains exactly as play would, until nothing is left. The paths are
//      disjoint and between them cover every tile.
//   2. Because they are disjoint, the letters can be chosen afterwards with complete
//      freedom - each path gets a real word of its own length, and no path can contradict
//      another.
//   3. Write those letters back into the cells the tiles started in.
//
// Play that list back in order and the board empties, which is the guarantee that lets
// the mode drop shuffle and hints.
//
// It promises the board HAS a solution, not that every route reaches one. Playing a word
// the generator did not plan for can strand the board. That is the game.
// ***************************************************************************************

import { Pcg32, GAME_RNG_STREAM } from './rng.ts';
import { settle, type Board } from './settle.ts';
import type { CommonWords } from './dictionary.ts';

/** Grid sizes from Puzzle_GetGridSizeForMode; game types are the enum in game/puzzle.h. */
export interface ModeBoardInfo {
  readonly gameType: number;
  readonly w: number;
  readonly h: number;
  /** True when the board is constructed rather than dealt, and so needs the common list. */
  readonly built: boolean;
}

export const BOARD_MODES: Readonly<Record<string, ModeBoardInfo>> = Object.freeze({
  daily: { gameType: 6, w: 9, h: 12, built: false },
  burst: { gameType: 7, w: 6, h: 6, built: false },
  puzzle: { gameType: 8, w: 5, h: 5, built: true },
  puzzlexxl: { gameType: 9, w: 9, h: 11, built: true },
  // LADDER. 3 + 4 + 5 + 6 + 7 = 25, which is exactly a 5x5 - so the five rungs carve the
  // whole board and finishing the ladder empties it. Game type 10 is new here; the C has
  // no such mode, so nothing upstream can disagree about the number.
  ladder: { gameType: 10, w: 5, h: 5, built: true },
  // HUNT. The same constructed 5x5 as Score Attack - carved backwards from real words, so
  // it can always be emptied - on its own game type, so it gets its own board each day.
  hunt: { gameType: 11, w: 5, h: 5, built: true },
  // FREEDOM. Also a constructed 5x5. The mode drops the adjacency rule, which is a rule
  // about how a word is PICKED, not about how the board is built - so the generator is
  // unchanged and only the game type differs, giving the mode its own board each day.
  freedom: { gameType: 12, w: 5, h: 5, built: true },
});

/** PUZZLEGEN_WORD_MIN / _MAX / _MAX_WORDS, game/puzzlegen.h. */
const PUZZLE_WORD_MIN = 3;
const PUZZLE_WORD_MAX = 6;
const PUZZLE_MAX_WORDS = 40;
const PUZZLE_MAX_ATTEMPTS = 600;
const PUZZLE_PATH_TRIES = 40;
const PUZZLE_MAX_CELLS = 12 * 12;

// The eight neighbours, in the order the C lists them. The ORDER matters: it is the array
// the Fisher-Yates shuffle permutes, so a different order is a different board.
const PUZZLE_STEP_X = [-1, 0, 1, -1, 1, -1, 0, 1];
const PUZZLE_STEP_Y = [-1, -1, -1, 0, 0, 1, 1, 1];

// ***************************************************************************************
// Puzzle_GetDailyNumber / Puzzle_GetDailySeedForDay, game/puzzle.cpp.
//
// These numbers are part of the wire format. TestDailyModes in the C pins day 20345 to
// 2462237152 for Daily and 3521098560 for Burst; test/parity.test.ts asserts the same
// values here, because two implementations of one number that nobody compares will
// eventually disagree and reject every honest score.
// ***************************************************************************************
export function dayNumberNow(now: number = Date.now()): number {
  return Math.floor(now / 1000 / 86400);
}

export function dailySeedForDay(mode: string, day: number): number {
  const info = BOARD_MODES[mode];

  if (!info) return 0;

  let h = Math.imul(day, 2654435761) >>> 0;

  h = (h ^ (Math.imul(info.gameType + 1, 2246822519) >>> 0)) >>> 0;

  h = (h ^ (h >>> 15)) >>> 0;
  h = Math.imul(h, 2246822519) >>> 0;
  h = (h ^ (h >>> 13)) >>> 0;
  h = Math.imul(h, 3266489917) >>> 0;
  h = (h ^ (h >>> 16)) >>> 0;

  return h === 0 ? 1 : h;
}

export interface BuiltBoard {
  readonly board: Board;
  readonly seed: number;
  readonly w: number;
  readonly h: number;
  /** The route the generator built it from. Word 0 is played first. */
  readonly words: readonly SolutionWord[];
}

export interface SolutionWord {
  readonly word: string;
  /** The path on the board AS IT WAS when that word was played, not on the start board. */
  readonly x: readonly number[];
  readonly y: readonly number[];
}

interface CarvedWord {
  length: number;
  /** Starting cell indexes, so letters land where the tiles began. */
  id: number[];
  x: number[];
  y: number[];
}

// ***************************************************************************************
// PuzzleGen_Build, minus the part that writes to the live grid.
// ***************************************************************************************
export function buildPuzzleBoard(
  seed: number,
  w: number,
  h: number,
  common: CommonWords | null,
): BuiltBoard | null {
  // NO WORD LIST, NO BOARD. Guessing here would mean verifying honest submissions against
  // a puzzle nobody was given, and rejecting all of them.
  if (!common || common.countOfLength(PUZZLE_WORD_MIN) === 0) return null;

  const rng = new Pcg32(seed, GAME_RNG_STREAM);

  let words: CarvedWord[] | null = null;

  for (let attempt = 0; attempt < PUZZLE_MAX_ATTEMPTS; attempt++) {
    words = carvePuzzle(rng, w, h, common);

    if (words !== null) break;
  }

  if (words === null) return null;

  const letters: Board = new Array<string | null>(w * h).fill(null);
  const solution: SolutionWord[] = [];

  for (const word of words) {
    const count = common.countOfLength(word.length);

    if (count === 0) return null;

    const text = common.wordOfLength(word.length, rng.bounded(count));

    if (!text || text.length !== word.length) return null;

    solution.push({ word: text, x: word.x.slice(), y: word.y.slice() });

    for (let i = 0; i < word.length; i++) letters[word.id[i]!] = text[i]!;
  }

  if (letters.some((c) => c === null)) return null;

  return { board: letters, seed, w, h, words: solution };
}

// ***************************************************************************************
// LADDER MODE's board.
//
// One word of each length 3, 4, 5, 6 and 7. They add up to 25, so on a 5x5 the five rungs
// between them cover every tile exactly once and completing the ladder clears the board.
//
// Same carve as the puzzle builder, with one difference: the lengths are FIXED rather
// than chosen as it goes. They are still carved in a random order, because the order a
// path is cut in decides what is left for the next one - carving the 7 first every time
// would make every board feel the same.
//
// WHAT THIS GUARANTEES, precisely: that a complete ladder EXISTS on the board. It does
// not guarantee that every order the player might try will work, because taking a word
// out collapses the grid under the others. Undo is free in this mode for exactly that
// reason.
// ***************************************************************************************
export const LADDER_RUNGS: readonly number[] = [3, 4, 5, 6, 7];

export function buildLadderBoard(
  seed: number,
  w: number,
  h: number,
  common: CommonWords | null,
): BuiltBoard | null {
  if (!common) return null;

  // Every rung needs words to draw on. Without the 7s in particular there is no ladder,
  // and a board built without one would be unfinishable by construction.
  for (const length of LADDER_RUNGS) {
    if (common.countOfLength(length) === 0) return null;
  }

  if (LADDER_RUNGS.reduce((total, n) => total + n, 0) !== w * h) return null;

  const rng = new Pcg32(seed, GAME_RNG_STREAM);

  for (let attempt = 0; attempt < PUZZLE_MAX_ATTEMPTS; attempt++) {
    const order = LADDER_RUNGS.slice();

    // Fisher-Yates, same shape as the neighbour shuffle in walkPuzzlePath.
    for (let i = order.length; i > 1; i--) {
      const j = rng.bounded(i);
      const t = order[i - 1]!;

      order[i - 1] = order[j]!;
      order[j] = t;
    }

    const carved = carveFixedLengths(rng, w, h, order);

    if (carved === null) continue;

    const letters: Board = new Array<string | null>(w * h).fill(null);
    const solution: SolutionWord[] = [];

    let ok = true;

    for (const word of carved) {
      const count = common.countOfLength(word.length);
      const text = common.wordOfLength(word.length, rng.bounded(count));

      if (!text || text.length !== word.length) {
        ok = false;
        break;
      }

      solution.push({ word: text, x: word.x.slice(), y: word.y.slice() });

      for (let i = 0; i < word.length; i++) letters[word.id[i]!] = text[i]!;
    }

    if (!ok || letters.some((c) => c === null)) continue;

    return { board: letters, seed, w, h, words: solution };
  }

  return null;
}

/** Carve paths of exactly these lengths, in this order, settling between each. */
function carveFixedLengths(
  rng: Pcg32,
  w: number,
  h: number,
  lengths: readonly number[],
): CarvedWord[] | null {
  const cell: (number | null)[] = new Array(w * h);

  for (let i = 0; i < w * h; i++) cell[i] = i;

  const words: CarvedWord[] = [];

  for (const wanted of lengths) {
    const path = findPuzzlePath(rng, cell, w, h, wanted);

    if (path === null) return null;

    const word: CarvedWord = { length: wanted, id: [], x: [], y: [] };

    for (const at of path) {
      word.id.push(cell[at]!);
      word.x.push(at % w);
      word.y.push(Math.floor(at / w));

      cell[at] = null;
    }

    words.push(word);

    if (!settle(cell as unknown as Board, w, h)) return null;
  }

  // Every tile must be spoken for, or the ladder does not clear the board.
  if (cell.some((c) => c !== null)) return null;

  return words;
}

// ***************************************************************************************
// Carve the grid into disjoint paths. Returns null if the search gave up.
// ***************************************************************************************
function carvePuzzle(
  rng: Pcg32,
  w: number,
  h: number,
  common: CommonWords,
): CarvedWord[] | null {
  const cell: (number | null)[] = new Array(w * h);

  for (let y = 0; y < h; y++) {
    for (let x = 0; x < w; x++) cell[y * w + x] = y * w + x;
  }

  const words: CarvedWord[] = [];

  for (;;) {
    let remaining = 0;

    for (let i = 0; i < cell.length; i++) {
      if (cell[i] !== null) remaining++;
    }

    if (remaining === 0) return words;

    if (words.length >= PUZZLE_MAX_WORDS) return null;

    // PuzzleGen_ChooseLength. Exactly one draw, and only when something is legal.
    const legal: number[] = [];

    for (let length = PUZZLE_WORD_MIN; length <= PUZZLE_WORD_MAX; length++) {
      if (length > remaining) break;

      const left = remaining - length;

      // One or two tiles stranded is an unfinishable puzzle, and one and two are the only
      // remainders no word can cover.
      if (left !== 0 && left < PUZZLE_WORD_MIN) continue;

      if (common.countOfLength(length) === 0) continue;

      legal.push(length);
    }

    if (legal.length === 0) return null;

    const wanted = legal[rng.bounded(legal.length)]!;

    const path = findPuzzlePath(rng, cell, w, h, wanted);

    if (path === null) return null;

    const word: CarvedWord = { length: wanted, id: [], x: [], y: [] };

    for (const at of path) {
      word.id.push(cell[at]!);
      word.x.push(at % w);
      word.y.push(Math.floor(at / w));

      cell[at] = null;
    }

    words.push(word);

    // Settle the CELL IDENTITIES exactly as play settles letters, so the carve sees the
    // board the player would.
    if (!settle(cell as unknown as Board, w, h)) return null;
  }
}

// ***************************************************************************************
// PuzzleGen_FindPath: a random occupied start, then a depth-first walk to the wanted
// length.
// ***************************************************************************************
function findPuzzlePath(
  rng: Pcg32,
  cell: (number | null)[],
  w: number,
  h: number,
  wanted: number,
): number[] | null {
  const used = new Array<boolean>(w * h).fill(false);

  for (let attempt = 0; attempt < PUZZLE_PATH_TRIES; attempt++) {
    used.fill(false);

    let x = 0;
    let y = 0;
    let found = false;

    // Rejection sampling for an occupied cell. The bound is the C's PUZZLE_MAX_CELLS.
    for (let pick = 0; pick < PUZZLE_MAX_CELLS * 2; pick++) {
      x = rng.bounded(w);
      y = rng.bounded(h);

      if (cell[y * w + x] !== null) {
        found = true;
        break;
      }
    }

    if (!found) return null;

    const path: number[] = [];

    if (walkPuzzlePath(rng, cell, used, path, w, h, x, y, 0, wanted)) return path;
  }

  return null;
}

// ***************************************************************************************
// PuzzleGen_Walk.
//
// Note the order of the two things it does: the terminal check comes BEFORE the neighbour
// shuffle, so the last tile of a path draws nothing. Moving the shuffle above it would
// consume an extra eight draws per path and change every board from that point on.
// ***************************************************************************************
function walkPuzzlePath(
  rng: Pcg32,
  cell: (number | null)[],
  used: boolean[],
  path: number[],
  w: number,
  h: number,
  x: number,
  y: number,
  depth: number,
  wanted: number,
): boolean {
  path[depth] = y * w + x;
  used[y * w + x] = true;

  if (depth + 1 === wanted) {
    path.length = wanted;
    return true;
  }

  // Fisher-Yates over the eight neighbours, exactly as the C does it: i counts DOWN from
  // 8 to 2, and each step draws once.
  const order = [0, 1, 2, 3, 4, 5, 6, 7];

  for (let i = 8; i > 1; i--) {
    const j = rng.bounded(i);
    const t = order[i - 1]!;

    order[i - 1] = order[j]!;
    order[j] = t;
  }

  for (let i = 0; i < 8; i++) {
    const nx = x + PUZZLE_STEP_X[order[i]!]!;
    const ny = y + PUZZLE_STEP_Y[order[i]!]!;

    if (nx < 0 || ny < 0) continue;
    if (nx >= w) continue;
    if (ny >= h) continue;
    if (cell[ny * w + nx] === null) continue;
    if (used[ny * w + nx]) continue;

    if (walkPuzzlePath(rng, cell, used, path, w, h, nx, ny, depth + 1, wanted)) return true;
  }

  used[y * w + x] = false;

  return false;
}

/**
 * The board for a given mode and day.
 *
 * Constructed modes need the common word list and return null without it, rather than
 * inventing a board nobody was playing on.
 */
export function generateBoard(
  mode: string,
  day: number,
  common: CommonWords | null,
): BuiltBoard | null {
  const info = BOARD_MODES[mode];

  if (!info) return null;

  const seed = dailySeedForDay(mode, day);

  if (mode === 'ladder') return buildLadderBoard(seed, info.w, info.h, common);

  if (info.built) return buildPuzzleBoard(seed, info.w, info.h, common);

  // The dealt modes are not built by this release. Returning null is honest; inventing a
  // dealt board here would produce a puzzle the C game would not agree with.
  return null;
}
